import { useUrlSearchParams } from '@vueuse/core'
import { getDocument } from '@/helpers/globals'
import { useObjectCookie } from '@/plugins/cookie-plugin'
import { type IntercomUser, useAuth } from '@/plugins/auth.ts'

export type IntercomBaseSettings = {
    app_id: string
    custom_launcher_selector: '.intercom-launcher'
    hide_default_launcher: true
}

export type IntercomCookieVariables = {
    taylorandhart_referrer?: string
    utm_source?: string
    utm_medium?: string
    utm_campaign?: string
    utm_content?: string
    utm_term?: string
}

export type IntercomSettings = IntercomBaseSettings &
    IntercomCookieVariables &
    IntercomUser

export function useIntercomSettings(APP_ID: string | undefined) {
    const document = getDocument()
    const { getIntercomUser } = useAuth()
    const params = useUrlSearchParams()

    const cookie = useObjectCookie<IntercomCookieVariables>(
        'taylorandhart_intercom'
    )

    const getIntercomCookieVariables = () => {
        const variables: IntercomCookieVariables = {}

        if (cookie.value) {
            Object.assign(variables, cookie.value)
        }

        if (document && !variables.taylorandhart_referrer) {
            variables.taylorandhart_referrer = document.referrer.substring(
                0,
                255
            )
        }

        if (params['utm_source']) {
            variables.utm_source = params['utm_source'] as string
        }

        if (params['utm_medium']) {
            variables.utm_medium = params['utm_medium'] as string
        }

        if (params['utm_campaign']) {
            variables.utm_campaign = params['utm_campaign'] as string
        }

        if (params['utm_content']) {
            variables.utm_content = params['utm_content'] as string
        }

        if (params['utm_term']) {
            variables.utm_term = params['utm_term'] as string
        }

        cookie.value = variables

        return variables
    }

    const getIntercomSettings = () => {
        const intercomSettings = {
            app_id: APP_ID,
            custom_launcher_selector: '.intercom-launcher',
            hide_default_launcher: true,
        } as IntercomSettings

        const intercomUser = getIntercomUser()

        if (intercomUser) {
            Object.assign(intercomSettings, intercomUser)
        }

        const intercomCookieVariables = getIntercomCookieVariables()

        Object.assign(intercomSettings, intercomCookieVariables)

        return intercomSettings
    }

    return {
        getIntercomSettings,
    }
}
